import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BannerHeader from "../../Component/Header/BannerHeader";
import Footer from "../../Component/Footer/Footer";
import mintingIcon from "../../assets/images/home/eth_s.svg";
import mapIcon from "../../assets/images/home/tiles.svg";
import collectiblesIcon from "../../assets/images/home/calculate.svg";
import "./help.css";

const categories = [
  { id: "all", name: "All topics" },
  { id: "getting-started", name: "Getting started" },
  { id: "minting", name: "Minting & land" },
  { id: "wallet", name: "Wallet & transactions" },
  { id: "geotag", name: "Maps & coordinates" },
  { id: "marketplace", name: "Marketplace" },
];

const helpArticles = [
  {
    id: 1,
    title: "What is Simule?",
    category: "getting-started",
    summary: "A quick introduction to digital land and how ownership works.",
    content: [
      "Simule turns real geographic locations into digital land collectibles. Each collectible represents a mapped parcel and records ownership on Ethereum.",
      "You can explore locations, mint available land, and manage the parcels connected to your wallet from the map experience.",
    ],
  },
  {
    id: 2,
    title: "Connect your wallet",
    category: "wallet",
    summary: "Connect MetaMask and confirm that you are using the right network.",
    content: [
      "Select Connect wallet in the header and approve the connection in MetaMask. Simule only requests access to view your public wallet address.",
      "Use the Ethereum Sepolia test network. Once connected, your shortened address and Sepolia ETH balance appear in the header.",
    ],
  },
  {
    id: 3,
    title: "Mint a new digital land",
    category: "minting",
    summary: "Select a map tile, choose its size, and complete the mint transaction.",
    content: [
      "Open the map, keep the Mint tab selected, and choose an available grid. Review its location, maximum area, available area, and price before continuing.",
      "Enter the number of square feet, select Start minting, and confirm the transaction in your wallet. The collectible appears in My Collectibles after confirmation.",
    ],
  },
  {
    id: 4,
    title: "Navigate and search the map",
    category: "geotag",
    summary: "Find a country, city, address, or landmark and inspect nearby grids.",
    content: [
      "Enter a place in the map search field and select one of the suggested results. The map moves to that location at a close zoom level so nearby grids are visible.",
      "Drag to move around, use the zoom controls for detail, and switch map styles when you need more geographic context.",
    ],
  },
  {
    id: 5,
    title: "Buy and sell on the marketplace",
    category: "marketplace",
    summary: "Understand listing, purchasing, and network confirmation.",
    content: [
      "To list a parcel, open the collectible you own, choose the marketplace action, set the price, and confirm the approval and listing transactions.",
      "Before purchasing a listing, verify the location, area, seller, and total wallet amount. Ethereum network fees are confirmed separately in your wallet.",
    ],
  },
  {
    id: 6,
    title: "Mint multiple lands in one batch",
    category: "minting",
    summary: "Select several available parcels and mint them together.",
    content: [
      "Choose Batch Mint on the map and select each grid you want to include. Selected parcels remain highlighted while you review the batch.",
      "Check the parcel count and combined total carefully before confirming. Your wallet will show the final transaction details.",
    ],
  },
  {
    id: 7,
    title: "Sepolia network requirements",
    category: "wallet",
    summary: "Why Simule uses Sepolia and what you need for transactions.",
    content: [
      "Simule currently operates on Ethereum Sepolia, a test network. Sepolia ETH has no real monetary value and is used only to pay test-network gas fees.",
      "If a transaction cannot start, check the selected network, wallet connection, Sepolia ETH balance, and whether another wallet request is already pending.",
    ],
  },
  {
    id: 8,
    title: "Manage your collectibles",
    category: "getting-started",
    summary: "Find, review, search, and transfer the land connected to your wallet.",
    content: [
      "My Collectibles loads the digital lands owned by the connected account. Use search and sorting to find a parcel, then open its actions for available ownership controls.",
      "When transferring, confirm the selected NFT image and token details, then enter a valid Ethereum address before approving the wallet transaction.",
    ],
  },
  {
    id: 9,
    title: "Understand X, Y, and Z coordinates",
    category: "geotag",
    summary: "How a token maps back to one precise map tile.",
    content: [
      "X and Y identify the map tile, while Z identifies its zoom level. Together they point to a precise geographic grid.",
      "These coordinates allow Simule to recreate the parcel image consistently from its token information across minting, collectibles, and transfer views.",
    ],
  },
  {
    id: 10,
    title: "Resolve a failed or pending transaction",
    category: "wallet",
    summary: "Practical checks for common wallet and RPC problems.",
    content: [
      "Confirm that MetaMask is unlocked, connected to Sepolia, and has enough Sepolia ETH for gas. Close duplicate wallet prompts before trying again.",
      "For a pending transaction, allow the network time to confirm it and check its status in the wallet. Avoid submitting the same action repeatedly while confirmation is in progress.",
    ],
  },
];

const popularTopics = [
  {
    category: "minting",
    label: "Minting digital land",
    description: "From selecting a grid to confirming the transaction.",
    icon: mintingIcon,
  },
  {
    category: "geotag",
    label: "Map & coordinates",
    description: "Search locations and understand land coordinates.",
    icon: mapIcon,
  },
  {
    category: "wallet",
    label: "Wallet support",
    description: "Connection, network, gas, and transaction guidance.",
    icon: collectiblesIcon,
  },
];

const Help = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openArticleId, setOpenArticleId] = useState(null);
  const articlesRef = useRef(null);

  const filteredArticles = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean);

    return helpArticles.filter((article) => {
      const matchesCategory =
        activeCategory === "all" || article.category === activeCategory;
      const searchableText = [
        article.title,
        article.summary,
        ...article.content,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        queryTerms.length === 0 ||
        queryTerms.every((term) => searchableText.includes(term));

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const scrollToArticles = () => {
    articlesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setOpenArticleId(null);
    window.setTimeout(scrollToArticles, 0);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setActiveCategory("all");
    setOpenArticleId(null);
    scrollToArticles();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setActiveCategory("all");
    setOpenArticleId(null);
  };

  return (
    <div className="help-page">
      <BannerHeader />

      <main>
        <section className="help-hero">
          <div className="help-shell help-hero-content">
            <span className="help-kicker">SIMULE SUPPORT</span>
            <h1>How can we help?</h1>
            <p>
              Clear guidance for exploring the map, minting digital land, and
              managing your wallet and collectibles.
            </p>

            <form className="help-search" onSubmit={handleSearch} role="search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m16.2 16.2 4 4" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search help topics"
                aria-label="Search the Simule Help Center"
              />
              <button type="submit">Search</button>
            </form>

            <div className="help-hero-meta" aria-label="Help Center details">
              <span>10 practical guides</span>
              <span>Sepolia network support</span>
              <span>Updated for the current Simule experience</span>
            </div>
          </div>
        </section>

        <section className="help-popular" aria-labelledby="popular-heading">
          <div className="help-shell">
            <div className="help-section-heading">
              <div>
                <span className="help-kicker">QUICK START</span>
                <h2 id="popular-heading">Popular help topics</h2>
              </div>
              <p>Start with the workflows people use most often.</p>
            </div>

            <div className="help-topic-grid">
              {popularTopics.map((topic) => (
                <button
                  type="button"
                  className="help-topic-card"
                  key={topic.category}
                  onClick={() => selectCategory(topic.category)}
                >
                  <span className="help-topic-icon">
                    <img src={topic.icon} alt="" aria-hidden="true" />
                  </span>
                  <span className="help-topic-copy">
                    <strong>{topic.label}</strong>
                    <span>{topic.description}</span>
                  </span>
                  <span className="help-topic-arrow" aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="help-library" ref={articlesRef}>
          <div className="help-shell help-library-layout">
            <aside className="help-categories" aria-label="Help categories">
              <div className="help-categories-title">Browse by category</div>
              <nav>
                {categories.map((category) => (
                  <button
                    type="button"
                    key={category.id}
                    className={activeCategory === category.id ? "active" : ""}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setOpenArticleId(null);
                    }}
                  >
                    <span>{category.name}</span>
                    <span>
                      {category.id === "all"
                        ? helpArticles.length
                        : helpArticles.filter(
                            (article) => article.category === category.id
                          ).length}
                    </span>
                  </button>
                ))}
              </nav>

              <div className="help-network-note">
                <span className="help-status-dot" aria-hidden="true" />
                <div>
                  <strong>Network reminder</strong>
                  <p>Simule currently uses Ethereum Sepolia.</p>
                </div>
              </div>
            </aside>

            <div className="help-articles">
              <div className="help-articles-header">
                <div>
                  <span className="help-kicker">KNOWLEDGE BASE</span>
                  <h2>
                    {activeCategory === "all"
                      ? "All help articles"
                      : categories.find(
                          (category) => category.id === activeCategory
                        )?.name}
                  </h2>
                </div>
                <span className="help-results-count">
                  {filteredArticles.length} result
                  {filteredArticles.length === 1 ? "" : "s"}
                </span>
              </div>

              {filteredArticles.length > 0 ? (
                <div className="help-article-list">
                  {filteredArticles.map((article) => {
                    const isOpen = openArticleId === article.id;
                    const categoryName = categories.find(
                      (category) => category.id === article.category
                    )?.name;

                    return (
                      <article className={`help-article ${isOpen ? "open" : ""}`} key={article.id}>
                        <button
                          type="button"
                          className="help-article-trigger"
                          aria-expanded={isOpen}
                          aria-controls={`help-article-${article.id}`}
                          onClick={() =>
                            setOpenArticleId(isOpen ? null : article.id)
                          }
                        >
                          <span className="help-article-number">
                            {String(article.id).padStart(2, "0")}
                          </span>
                          <span className="help-article-heading">
                            <strong>{article.title}</strong>
                            <span>{article.summary}</span>
                          </span>
                          <span className="help-article-toggle" aria-hidden="true">
                            {isOpen ? "−" : "+"}
                          </span>
                        </button>

                        {isOpen && (
                          <div
                            className="help-article-content"
                            id={`help-article-${article.id}`}
                          >
                            {article.content.map((paragraph) => (
                              <p key={paragraph}>{paragraph}</p>
                            ))}
                            <span className="help-article-category">
                              {categoryName}
                            </span>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="help-empty-state">
                  <span aria-hidden="true">?</span>
                  <h3>No matching help articles</h3>
                  <p>Try a shorter search or return to all help topics.</p>
                  <button type="button" onClick={clearFilters}>
                    View all topics
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="help-final-cta">
          <div className="help-shell">
            <div className="help-final-card">
              <div>
                <span className="help-kicker">READY TO CONTINUE?</span>
                <h2>Put the guidance into practice on the map.</h2>
                <p>
                  Explore locations, review parcel details, and mint with clear
                  transaction information at every step.
                </p>
              </div>
              <div className="help-final-actions">
                <Link to="/map" className="help-primary-link">
                  Open the map <span aria-hidden="true">↗</span>
                </Link>
                <Link to="/" className="help-secondary-link">Back to home</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Help;
